import * as React from 'react';

import type {FilterObject, ComparisonRangeType} from 'nutshell-core/types';

import {analyticsClient} from '../../analytics-client';
import {appHistory} from '../../history';

import {
    FORECAST,
    NO_SEGMENT_ID,
    getReportDefaultSegment,
    getReportDefaultState,
    getShouldAllowSegmentation,
    getReportMinDate,
    getReportMaxDate,
} from './report-constants';
import {ReportControls} from './report-controls';
import type {ReportKey, ReportFacet, ReportSegment, ChartReportParams} from './report-types';

const FALLBACK_DATE_RANGE_TYPE = 'period';

type ReportControlsContainerProps = {
    reportKey: ReportKey;
    reportParams?: ChartReportParams;
    activeFilters: Array<FilterObject>;
    onParamChange: (...args: any) => any;
    noPadding?: boolean;
};

export class ReportControlsContainer extends React.PureComponent<ReportControlsContainerProps> {
    render() {
        const {activeFilters, reportParams, reportKey, noPadding} = this.props;

        const segment =
            reportParams && reportParams.segment
                ? reportParams.segment
                : getReportDefaultSegment(reportKey);

        const facet = reportParams ? reportParams.facet : undefined;

        const shouldUseConfidence = reportParams
            ? reportParams.facet === 'total' && reportParams.useConfidence === 'true'
            : false;

        const comparisonType = reportParams ? reportParams.compareTo : undefined;

        return (
            <ReportControls
                reportKey={reportKey}
                dateRangeType={
                    (reportParams && reportParams.dateRangeType) || FALLBACK_DATE_RANGE_TYPE
                }
                activeFilters={activeFilters}
                segment={getShouldAllowSegmentation(reportKey) ? segment : null}
                hasConfidenceToggle={reportKey === FORECAST}
                facet={facet}
                useConfidence={shouldUseConfidence}
                comparisonType={comparisonType}
                onFacetChange={this.handleFacetChange}
                onUseConfidenceChange={this.handleUseConfidenceChange}
                onSegmentChange={this.handleSegmentChange}
                onFilterChange={this.handleFilterChange}
                onRemoveAllFilters={() => this.handleRemoveAllFilters(reportKey)}
                onDateRangeChange={this.handleDateRangeChange}
                minDate={getReportMinDate(reportKey)}
                maxDate={getReportMaxDate(reportKey)}
                noPadding={noPadding}
            />
        );
    }

    handleSegmentChange = (segment: {id: string}) => {
        if (segment.id === NO_SEGMENT_ID) {
            this.props.onParamChange({report: {segment: ''}}, true);
        } else {
            this.props.onParamChange({report: {segment: segment.id}}, true);
        }
    };

    handleFacetChange = (facet: ReportFacet) => {
        this.props.onParamChange({report: {facet}}, true);
    };

    handleDetailViewSegmentChange = (segment: ReportSegment) => {
        this.props.onParamChange({report: {segment}}, true);
    };

    handleUseConfidenceChange = (useConfidence: boolean) => {
        this.props.onParamChange({report: {useConfidence: String(useConfidence)}}, true);
    };

    handleFilterChange = (newFilterObject: FilterObject) => {
        this.props.onParamChange({filter: newFilterObject}, false);
    };

    handleRemoveAllFilters = (reportKey: ReportKey) => {
        const defaultSearch = getReportDefaultState(reportKey).search;

        appHistory.replace(defaultSearch);
    };

    handleDateRangeChange = (newDateFilter: FilterObject, comparisonType?: ComparisonRangeType) => {
        const newParams = {filter: newDateFilter, report: {compareTo: comparisonType}} as const;
        this.props.onParamChange(newParams, true);
        if (this.props.reportKey === 'chatbot') {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_TIME_FILTER_CHANGED_IN_CR);
        }
    };
}
